import React from 'react';
import FramePage from 'components/framePage/FramePage';
import Error404 from 'screens/errors/404/Error404';
import { getItem } from 'helpers/storage';
import { getUserData } from 'helpers/user/getUserData';
import { getUserAccountType } from 'helpers/user/getUserAccountType';
import { useTranslation } from 'react-i18next';
import { AccessType } from '../enums/AccessType';
import { MicroAppEmbed } from './MicroAppEmbed';
import { getFlag } from 'helpers/featureFlags/featureFlags';

const GLOBAL_NAME = 'MyAuditTrends';
const BUNDLE_FILE_NAME = 'my-audit-trends.js';
const MOUNT_BASENAME = '/myLQA/my-audit-trends';
const MISSING_BUNDLE_URL_MESSAGE = 'MICRO_APP_MY_AUDIT_TRENDS_URL is not configured';

/**
 * Origin from env + IIFE filename. Returns null when unset so we never resolve
 * to a relative `/my-audit-trends.js` on the admin origin.
 */
function resolveBundleUrl(rawUrl: unknown): string | null {
    const origin = typeof rawUrl === 'string' ? rawUrl.trim() : '';
    if (origin.length === 0) {
        return null;
    }
    return `${origin.replace(/\/?$/, '/')}${BUNDLE_FILE_NAME}`;
}

// Client/hotel user types allowed to reach the page. Mirrors the
// `allowedUserTypes` the nav uses for `/myLQA/my-audit-trends`
// (see getMenuItems.ts).
const ALLOWED_ACCESS_TYPES: AccessType[] = [
    AccessType.brandClientUser,
    AccessType.clientUser,
    AccessType.masterClientUser,
    AccessType.regionalClientUser,
    AccessType.hotelUser,
];

function isUserAllowedToMyAuditTrends(userData: unknown): boolean {
    if (!userData) {
        return false;
    }
    const accountType = getUserAccountType(userData);
    return ALLOWED_ACCESS_TYPES.some((allowedType) => allowedType === accountType);
}

function MyAuditTrendsRemote() {
    const { t } = useTranslation();
    const title = t('MY_AUDIT.LIST.TITLE');
    const bundleUrl = resolveBundleUrl(Env.MICRO_APP_MY_AUDIT_TRENDS_URL);

    if (bundleUrl) {
        return (
            <FramePage title={title}>
                <div data-testid="myAuditTrends-embed">
                    <MicroAppEmbed bundleUrl={bundleUrl} globalName={GLOBAL_NAME} basename={MOUNT_BASENAME} />
                </div>
            </FramePage>
        );
    }

    return (
        <FramePage title={title}>
            <div data-testid="myAuditTrends-missingBundleUrl" style={{ padding: 24, color: '#b00020' }}>
                {MISSING_BUNDLE_URL_MESSAGE}
            </div>
        </FramePage>
    );
}

// Route entry point. Restricts the page to the same users as the existing
// myAudit Overview BETA menu item; unauthorized users get the 404 page and the
// remote bundle is never loaded for them.
export function MyAuditTrends() {
    const isAllowed =
        getFlag('isMyAuditOverviewEnabled') && Boolean(getItem('user')) && isUserAllowedToMyAuditTrends(getUserData(false));

    if (isAllowed) {
        return <MyAuditTrendsRemote />;
    }

    return <Error404 />;
}
